/**
 * Controle de inadimplência.
 *
 * Dois recortes, escolhidos no topo:
 *
 *  · CARTEIRA — só ATIVO e INADIMPLENTE. É a cobrança do dia a dia e a base de
 *    todos os indicadores: taxas por placas e por valor, faixas de atraso
 *    (aging), safra por mês de vencimento, evolução e dia de vencimento.
 *  · SAÍRAM COM DÍVIDA — inativos, negados e sem cadastro com boleto em
 *    aberto. Fica fora das taxas: é recuperação ou baixa, não carteira.
 *
 * Pendente revistoria tem tela própria — quem está ali já pagou.
 *
 * Indicadores clicáveis (faixa, dia, cartão) recortam a lista de cobrança
 * logo abaixo, para que todo número leve a quem cobrar.
 *
 * A tabela é responsiva por troca de forma, não por rolagem: no celular vira
 * uma lista de cartões, porque tabela de 8 colunas em 360px não se lê nem
 * rolando na horizontal.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Check,
  ChevronRight,
  Copy,
  FileDown,
  Percent,
  Printer,
  ShieldAlert,
  UserX,
  Wallet,
} from 'lucide-react';
import { api } from '../../lib/api.ts';
import {
  formatarCpf,
  moeda,
  moedaCompacta,
  numero,
  percentual,
} from '../../lib/format.ts';
import { exportarPdf, exportarPlanilha, exportarTexto } from '../../lib/exportar.ts';
import {
  Aviso,
  Botao,
  Card,
  Esqueleto,
  EstadoVazio,
  Segmentado,
  TituloSecao,
} from '../../components/ui/index.tsx';
import Folha from '../../components/ui/Folha.tsx';
import {
  BarraComposicao,
  CartaoDado,
  GradeBento,
  MiniDados,
} from '../../components/ui/Bento.tsx';
import { LinhasTaxa } from '../../components/charts/index.tsx';
import {
  SeletorOrdem,
  ThOrdenavel,
  TituloOrdenavel,
  useOrdenacao,
} from '../../components/ui/Ordenacao.tsx';
import BarraFiltros, {
  filtroInicial,
  type OpcoesFiltro,
  type ValorFiltro,
} from '../../components/filtros/BarraFiltros.tsx';
import { cx } from '../../lib/cx.ts';
import {
  AbasDetalhe,
  BotoesContato,
  Dado,
  DadosAssociado,
  DadosVeiculo,
  dataBr,
  Etiquetas,
  GradeDados,
  LinhaFaixa,
  mesCurto,
  type Associado,
} from './cobranca.tsx';

type Escopo = 'carteira' | 'fora';

type FaixaAtraso = 'ate5' | 'ate15' | 'ate30' | 'ate60' | 'ate90' | 'mais90';

interface Item {
  placa: string | null;
  titulos: number;
  /** A parte desta placa no título — é o que soma sem contar dinheiro duas vezes. */
  valor: number;
  /** O título inteiro, que é o valor de fato cobrado. Difere na frota. */
  valorTitulo: number;
  diasAtraso: number;
  faixa: FaixaAtraso;
  vencimentoMaisAntigo: string | null;
  diaVencimento: number;
  linhaDigitavel: string | null;
  classe: 'nunca_pagou' | 'parou_de_pagar';
  boletosPagos: number;
  boletosEmAberto: number;
  associado: Associado;
  veiculo: { marca: string | null; modelo: string | null; situacao: string | null };
  cooperativa: string | null;
  consultor: string | null;
  ativoComAtraso: boolean;
}

interface RespostaLista {
  resumo: {
    placas: number;
    titulos: number;
    valor: number;
    nuncaPagou: number;
    ativoComAtraso: number;
  };
  janela: { de: string | null; ate: string | null };
  itens: Item[];
}

interface MesSafra {
  mes: string;
  placas: number;
  placasAtrasaram: number;
  placasEmAberto: number;
  emitido: number;
  pagoEmDia: number;
  pagoAtrasado: number;
  emAberto: number;
}

interface DiaVencimento {
  dia: number;
  emitido: number;
  atrasou: number;
  emAberto: number;
  placasEmAbertoHoje: number;
  valorEmAbertoHoje: number;
}

interface Painel {
  carteira: { placas: number; inadimplentes: number; valor: number; titulos: number };
  faixas: { id: FaixaAtraso; rotulo: string; placas: number; titulos: number; valor: number }[];
  meses: MesSafra[];
  dias: DiaVencimento[];
}

type Recorte =
  | { tipo: 'todos' }
  | { tipo: 'faixa'; faixa: FaixaAtraso; rotulo: string }
  | { tipo: 'dia'; dia: number }
  | { tipo: 'nunca_pagou' }
  | { tipo: 'ativo_com_atraso' };

const TODOS: Recorte = { tipo: 'todos' };

/** A mesma régua da revistoria: até 5 dias de atraso ainda é "pagar perto do dia". */
const DIAS_CARENCIA = 5;

/** Do amarelo ao vermelho: quanto mais velha a dívida, mais quente. */
const COR_FAIXA: Record<FaixaAtraso, string> = {
  ate5: 'var(--chart-2)',
  ate15: 'var(--warn)',
  ate30: 'color-mix(in oklab, var(--warn) 50%, var(--danger))',
  ate60: 'var(--danger)',
  ate90: 'color-mix(in oklab, var(--danger) 80%, black)',
  mais90: 'color-mix(in oklab, var(--danger) 60%, black)',
};

const pct = (parte: number, todo: number): number | null => (todo > 0 ? (parte / todo) * 100 : null);

function descreverRecorte(r: Recorte): string {
  switch (r.tipo) {
    case 'faixa':
      return `atraso de ${r.rotulo}`;
    case 'dia':
      return `vencimento dia ${String(r.dia).padStart(2, '0')}`;
    case 'nunca_pagou':
      return 'nunca pagou';
    case 'ativo_com_atraso':
      return 'ativo com mais de 5 dias';
    default:
      return '';
  }
}

export default function Inadimplentes() {
  const [filtro, setFiltro] = useState<ValorFiltro>(filtroInicial);
  const [escopo, setEscopo] = useState<Escopo>('carteira');
  const [recorte, setRecorte] = useState<Recorte>(TODOS);
  const [aviso, setAviso] = useState<string | null>(null);
  const listaRef = useRef<HTMLDivElement>(null);

  const params = new URLSearchParams();
  if (filtro.cooperativa) params.set('cooperativa', filtro.cooperativa);
  if (filtro.consultor) params.set('consultor', filtro.consultor);
  const qs = params.toString();

  const { data: opcoes } = useQuery({
    queryKey: ['vendas', 'filtros'],
    queryFn: () => api.get<OpcoesFiltro>('/vendas/filtros'),
    staleTime: 10 * 60_000,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['inadimplentes', escopo, qs],
    queryFn: () => api.get<RespostaLista>(`/inadimplentes?escopo=${escopo}&${qs}`),
  });

  const { data: painel } = useQuery({
    queryKey: ['inadimplentes', 'painel', qs],
    queryFn: () => api.get<Painel>(`/inadimplentes/painel?${qs}`),
    enabled: escopo === 'carteira',
  });

  /** Recorta a lista e leva o olho até ela — o número virou gente para cobrar. */
  const recortar = useCallback((r: Recorte) => {
    setRecorte((atual) => (JSON.stringify(atual) === JSON.stringify(r) ? TODOS : r));
    requestAnimationFrame(() =>
      listaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    );
  }, []);

  const itens = useMemo(() => {
    const todos = data?.itens ?? [];
    switch (recorte.tipo) {
      case 'faixa':
        return todos.filter((i) => i.faixa === recorte.faixa);
      case 'dia':
        return todos.filter((i) => i.diaVencimento === recorte.dia);
      case 'nunca_pagou':
        return todos.filter((i) => i.classe === 'nunca_pagou');
      case 'ativo_com_atraso':
        return todos.filter((i) => i.ativoComAtraso);
      default:
        return todos;
    }
  }, [data, recorte]);

  const titulo = escopo === 'carteira' ? 'Inadimplentes' : 'Saíram com dívida';
  const totalItens = itens.reduce((s, i) => s + i.valor, 0);

  async function copiarTexto() {
    const linhas = itens.map(
      (i) =>
        `• *${i.associado.nome ?? 'Sem nome'}* — ${i.placa ?? 's/ placa'}\n` +
        `  ${i.titulos} título(s) · ${moeda(i.valor)} · ${i.diasAtraso} dias\n` +
        `  ${i.associado.celular ?? i.associado.telefone ?? 'sem telefone'}`,
    );
    const cabecalho =
      `*${titulo}*${recorte.tipo !== 'todos' ? ` · ${descreverRecorte(recorte)}` : ''} — ` +
      `${itens.length} placa(s), ${moeda(totalItens)}\n` +
      `_Gerado em ${new Date().toLocaleString('pt-BR')}_\n\n`;
    const r = await exportarTexto(cabecalho + linhas.join('\n\n'), titulo);
    setAviso(
      r.compartilhado ? null : r.ok ? 'Lista copiada — é só colar no WhatsApp.' : 'Não consegui copiar.',
    );
  }

  function baixarPlanilha() {
    exportarPlanilha(
      [
        { chave: 'associado', titulo: 'Associado' },
        { chave: 'cpf', titulo: 'CPF' },
        { chave: 'celular', titulo: 'Celular' },
        { chave: 'placa', titulo: 'Placa' },
        { chave: 'veiculo', titulo: 'Veículo' },
        { chave: 'situacao', titulo: 'Situação do veículo' },
        { chave: 'titulos', titulo: 'Títulos' },
        { chave: 'valor', titulo: 'Valor' },
        { chave: 'vencimento', titulo: 'Vencido desde' },
        { chave: 'dias', titulo: 'Dias em atraso' },
        { chave: 'faixa', titulo: 'Faixa de atraso' },
        { chave: 'classe', titulo: 'Histórico' },
        { chave: 'cooperativa', titulo: 'Cooperativa' },
        { chave: 'consultor', titulo: 'Consultor' },
        { chave: 'cidade', titulo: 'Cidade' },
      ],
      itens.map((i) => ({
        associado: i.associado.nome,
        cpf: formatarCpf(i.associado.cpf),
        celular: i.associado.celular ?? i.associado.telefone,
        placa: i.placa,
        veiculo: [i.veiculo.marca, i.veiculo.modelo].filter(Boolean).join(' '),
        situacao: i.veiculo.situacao,
        titulos: i.titulos,
        // Vírgula decimal: é o que o Excel em português entende como número.
        valor: i.valor.toFixed(2).replace('.', ','),
        vencimento: dataBr(i.vencimentoMaisAntigo),
        dias: i.diasAtraso,
        faixa: painel?.faixas.find((f) => f.id === i.faixa)?.rotulo ?? i.faixa,
        classe: i.classe === 'nunca_pagou' ? 'Nunca pagou' : 'Parou de pagar',
        cooperativa: i.cooperativa,
        consultor: i.consultor,
        cidade: [i.associado.cidade, i.associado.estado].filter(Boolean).join(' · '),
      })),
      escopo === 'carteira' ? 'inadimplentes' : 'sairam-com-divida',
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="imprimir-oculto flex flex-col gap-3">
        <BarraFiltros valor={filtro} onChange={setFiltro} opcoes={opcoes} semPeriodo />
        <div className="scroll-x">
          <Segmentado
            rotulo="Recorte da inadimplência"
            valor={escopo}
            onChange={(v) => {
              setEscopo(v);
              setRecorte(TODOS);
            }}
            opcoes={[
              { valor: 'carteira', rotulo: 'Carteira · ativos e inadimplentes' },
              { valor: 'fora', rotulo: 'Saíram com dívida' },
            ]}
          />
        </div>
      </div>

      {/* Cabeçalho que só aparece no PDF. */}
      <div className="hidden imprimir-visivel">
        <h1 className="text-lg font-semibold">
          {titulo}
          {recorte.tipo !== 'todos' && ` · ${descreverRecorte(recorte)}`}
        </h1>
        <p className="text-[13px] text-[var(--muted)]">
          {itens.length} placas · {moeda(totalItens)} · {new Date().toLocaleString('pt-BR')}
        </p>
      </div>

      {isLoading || (escopo === 'carteira' && !painel) ? (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <Esqueleto key={i} className="h-28" />
            ))}
          </div>
          <Esqueleto className="h-64" />
        </div>
      ) : escopo === 'carteira' && painel && data ? (
        <PainelCarteira painel={painel} resumo={data.resumo} recorte={recorte} recortar={recortar} />
      ) : data ? (
        <ResumoFora resumo={data.resumo} recorte={recorte} recortar={recortar} />
      ) : null}

      {data?.janela?.de && (
        <Aviso>
          Os boletos sincronizados começam em{' '}
          {new Date(`${data.janela.de}T12:00`).toLocaleDateString('pt-BR')}: “nunca pagou” e a
          série mensal valem a partir daí, não desde a adesão.
        </Aviso>
      )}

      <div ref={listaRef} className="imprimir-oculto flex scroll-mt-4 flex-wrap items-center gap-2">
        <h2 className="mr-auto text-sm font-medium text-[var(--fg)]">
          Lista de cobrança
          {recorte.tipo !== 'todos' && (
            <span className="text-[var(--muted)]"> · {descreverRecorte(recorte)}</span>
          )}
        </h2>
        <span className="text-[13px] text-[var(--muted)]">
          {numero(itens.length)} de {numero(data?.itens.length ?? 0)}
        </span>
      </div>

      <div className="imprimir-oculto flex flex-wrap items-center gap-2">
        <Botao tamanho="sm" icone={<Copy size={14} />} onClick={() => void copiarTexto()}>
          Texto / WhatsApp
        </Botao>
        <Botao tamanho="sm" icone={<FileDown size={14} />} onClick={baixarPlanilha}>
          Planilha
        </Botao>
        <Botao tamanho="sm" icone={<Printer size={14} />} onClick={exportarPdf}>
          PDF
        </Botao>
        {recorte.tipo !== 'todos' && (
          <Botao tamanho="sm" variante="fantasma" onClick={() => setRecorte(TODOS)}>
            Limpar recorte
          </Botao>
        )}
      </div>

      {aviso && <Aviso>{aviso}</Aviso>}

      {!isLoading &&
        (itens.length === 0 ? (
          <Card className="p-2">
            <EstadoVazio
              icone={<AlertTriangle size={20} />}
              titulo="Nenhum inadimplente neste recorte"
              descricao="Ou a carteira está em dia, ou os filtros acima estão restritos demais."
            />
          </Card>
        ) : (
          <Tabela itens={itens} />
        ))}
    </div>
  );
}

// ─────────────────────────── Painel da carteira ───────────────────────────

function PainelCarteira({
  painel,
  resumo,
  recorte,
  recortar,
}: {
  painel: Painel;
  resumo: RespostaLista['resumo'];
  recorte: Recorte;
  recortar: (r: Recorte) => void;
}) {
  const c = painel.carteira;

  // Taxa por valor do último mês FECHADO — o que já passou dos 5 dias de
  // carência até no último vencimento. No dia 2, outubro ainda tem os
  // vencimentos de 25 e 30 recém-vencidos e a taxa sairia inflada.
  const carencia = new Date();
  carencia.setDate(carencia.getDate() - DIAS_CARENCIA);
  const mesFechado = carencia.toLocaleDateString('en-CA', { timeZone: 'America/Cuiaba' }).slice(0, 7);
  const fechados = painel.meses.filter((m) => m.mes < mesFechado);
  const ultimo = fechados.at(-1) ?? painel.meses.at(-1);
  const taxaPlacas = pct(c.inadimplentes, c.placas);

  const maxFaixa = Math.max(...painel.faixas.map((f) => f.valor), 1);
  const acima30 = painel.faixas
    .filter((f) => f.id === 'ate60' || f.id === 'ate90' || f.id === 'mais90')
    .reduce((s, f) => s + f.valor, 0);

  return (
    <>
      <GradeBento>
        <CartaoDado
          tamanho="destaque"
          rotulo="Em aberto na carteira"
          valor={moeda(c.valor)}
          nota={`${numero(c.inadimplentes)} placas inadimplentes`}
          Icone={Wallet}
          tom="perigo"
        >
          <BarraComposicao
            fatias={painel.faixas.map((f) => ({
              rotulo: f.rotulo.replace(' dias', 'd').replace('mais de ', '+'),
              valor: f.valor,
              cor: COR_FAIXA[f.id],
              texto: moedaCompacta(f.valor),
            }))}
          />
          <div className="mt-4">
            <MiniDados
              itens={[
                {
                  rotulo: 'Média por placa',
                  valor: c.inadimplentes ? moeda(c.valor / c.inadimplentes) : '—',
                },
                { rotulo: 'Títulos vencidos', valor: numero(c.titulos) },
                {
                  rotulo: 'Há mais de 30 dias',
                  valor: percentual(pct(acima30, c.valor) ?? 0, 0),
                  tom: acima30 > 0 ? 'perigo' : 'neutro',
                },
              ]}
            />
          </div>
        </CartaoDado>
        <CartaoDado
          rotulo="Taxa por placas"
          valor={taxaPlacas === null ? '—' : percentual(taxaPlacas, 1)}
          nota={`${numero(c.inadimplentes)} de ${numero(c.placas)} placas da carteira`}
          Icone={Percent}
          tom="alerta"
        />
        <CartaoDado
          rotulo={`Taxa por valor · ${ultimo ? mesCurto(ultimo.mes) : '—'}`}
          valor={
            ultimo && ultimo.emitido > 0 ? percentual(pct(ultimo.emAberto, ultimo.emitido), 1) : '—'
          }
          nota={
            ultimo
              ? `${moedaCompacta(ultimo.emAberto)} em aberto de ${moedaCompacta(ultimo.emitido)} · ${percentual(pct(ultimo.emAberto + ultimo.pagoAtrasado, ultimo.emitido), 0)} não pagou no vencimento`
              : undefined
          }
          Icone={Percent}
          tom="alerta"
        />
        <CartaoDado
          rotulo="Nunca pagou"
          valor={numero(resumo.nuncaPagou)}
          nota="nenhum boleto quitado"
          Icone={UserX}
          tom="perigo"
          ativo={recorte.tipo === 'nunca_pagou'}
          onClick={() => recortar({ tipo: 'nunca_pagou' })}
        />
        <CartaoDado
          rotulo="Ativo com +5 dias"
          valor={numero(resumo.ativoComAtraso)}
          nota="protegido sem pagar — deveria estar inadimplente"
          Icone={ShieldAlert}
          tom="alerta"
          ativo={recorte.tipo === 'ativo_com_atraso'}
          onClick={() => recortar({ tipo: 'ativo_com_atraso' })}
        />
      </GradeBento>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-5">
          <TituloSecao>Faixas de atraso</TituloSecao>
          <div className="-mx-2 flex flex-col gap-1">
            {painel.faixas.map((f) => (
              <LinhaFaixa
                key={f.id}
                rotulo={f.rotulo}
                placas={f.placas}
                valor={moeda(f.valor)}
                fracao={f.valor / maxFaixa}
                cor={COR_FAIXA[f.id]}
                ativo={recorte.tipo === 'faixa' && recorte.faixa === f.id}
                onClick={() => recortar({ tipo: 'faixa', faixa: f.id, rotulo: f.rotulo })}
              />
            ))}
          </div>
          <p className="mt-3 text-xs text-[var(--faint)]">
            Pelo boleto mais antigo em aberto de cada placa. Toque numa faixa para ver quem cobrar.
          </p>
        </Card>

        <Card className="p-5">
          <TituloSecao>Evolução da inadimplência</TituloSecao>
          <LinhasTaxa
            meses={painel.meses.map((m) => ({
              rotulo: mesCurto(m.mes),
              valores: {
                valor: pct(m.emAberto, m.emitido),
                placas: pct(m.placasEmAberto, m.placas),
                recuperacao: pct(m.pagoAtrasado, m.pagoAtrasado + m.emAberto),
              },
            }))}
            series={[
              { chave: 'valor', rotulo: 'Por valor', cor: 'var(--danger)' },
              { chave: 'placas', rotulo: 'Por placas', cor: 'var(--warn)' },
              { chave: 'recuperacao', rotulo: 'Recuperação', cor: 'var(--success)' },
            ]}
          />
        </Card>
      </div>

      <TabelaMeses meses={painel.meses} />

      <DiasVencimento dias={painel.dias} recorte={recorte} recortar={recortar} />
    </>
  );
}

/**
 * Safra por mês de vencimento: o que foi emitido em cada mês e o destino de
 * cada real. A leitura de mercado em duas taxas:
 *  · NO VENCIMENTO (bruta) — quanto não estava pago no dia;
 *  · HOJE (líquida) — quanto segue em aberto depois da cobrança.
 * A distância entre as duas é a recuperação.
 */
function TabelaMeses({ meses }: { meses: MesSafra[] }) {
  const { ordenados: linhas, ordem, alternar, definir } = useOrdenacao(
    meses,
    {
      mes: (m) => m.mes,
      emitido: (m) => m.emitido,
      pagoEmDia: (m) => m.pagoEmDia,
      pagoAtrasado: (m) => m.pagoAtrasado,
      emAberto: (m) => m.emAberto,
      noVencimento: (m) => pct(m.pagoAtrasado + m.emAberto, m.emitido),
      hoje: (m) => pct(m.emAberto, m.emitido),
      recuperacao: (m) => pct(m.pagoAtrasado, m.pagoAtrasado + m.emAberto),
    },
    { coluna: 'mes', desc: true },
  );
  const th = { ordem, onOrdenar: alternar };

  return (
    <Card className="p-0">
      <div className="p-5 pb-0">
        <TituloSecao>Por mês de vencimento</TituloSecao>
      </div>

      {/* Oito colunas de dinheiro só cabem sem rolagem a partir de 1280px;
          abaixo disso, cada mês vira uma linha resumida. */}
      <div className="hidden xl:block">
        <table className="w-full table-fixed text-[13px]">
          <thead className="border-y border-[var(--border)] bg-[var(--bg-subtle)] text-[var(--muted)]">
            <tr>
              <ThOrdenavel coluna="mes" className="w-24" {...th}>Mês</ThOrdenavel>
              <ThOrdenavel coluna="emitido" direita {...th}>Emitido</ThOrdenavel>
              <ThOrdenavel coluna="pagoEmDia" direita {...th}>Pago em dia</ThOrdenavel>
              <ThOrdenavel coluna="pagoAtrasado" direita {...th}>Pago c/ atraso</ThOrdenavel>
              <ThOrdenavel coluna="emAberto" direita {...th}>Em aberto</ThOrdenavel>
              <ThOrdenavel coluna="noVencimento" direita {...th}>No venc.</ThOrdenavel>
              <ThOrdenavel coluna="hoje" direita {...th}>Hoje</ThOrdenavel>
              <ThOrdenavel coluna="recuperacao" direita {...th}>Recuperação</ThOrdenavel>
            </tr>
          </thead>
          <tbody>
            {linhas.map((m) => (
              <tr key={m.mes} className="border-b border-[var(--border)] last:border-0">
                <td className="p-3 font-medium text-[var(--fg)]">{mesCurto(m.mes)}</td>
                <td className="num p-3 text-right">{moeda(m.emitido)}</td>
                <td className="num p-3 text-right text-[var(--muted)]">{moeda(m.pagoEmDia)}</td>
                <td className="num p-3 text-right text-[var(--muted)]">{moeda(m.pagoAtrasado)}</td>
                <td className="num p-3 text-right text-[var(--danger)]">{moeda(m.emAberto)}</td>
                <td className="num p-3 text-right">
                  {fmtPct(pct(m.pagoAtrasado + m.emAberto, m.emitido))}
                </td>
                <td className="num p-3 text-right font-semibold text-[var(--danger)]">
                  {fmtPct(pct(m.emAberto, m.emitido))}
                </td>
                <td className="num p-3 text-right text-[var(--success)]">
                  {fmtPct(pct(m.pagoAtrasado, m.pagoAtrasado + m.emAberto))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SeletorOrdem
        className="px-5 pb-3 xl:hidden"
        ordem={ordem}
        onChange={definir}
        opcoes={[
          { coluna: 'mes', rotulo: 'Mês' },
          { coluna: 'emitido', rotulo: 'Emitido' },
          { coluna: 'emAberto', rotulo: 'Em aberto' },
          { coluna: 'hoje', rotulo: '% em aberto hoje' },
          { coluna: 'noVencimento', rotulo: '% no vencimento' },
          { coluna: 'recuperacao', rotulo: 'Recuperação' },
        ]}
      />
      <ul className="m-0 grid list-none p-0 md:grid-cols-2 xl:hidden">
        {linhas.map((m) => (
          <li key={m.mes} className="border-t border-[var(--border)] px-5 py-3 text-[13px]">
            <div className="flex items-baseline justify-between gap-3">
              <span className="font-medium text-[var(--fg)]">{mesCurto(m.mes)}</span>
              <span className="num text-[var(--muted)]">{moeda(m.emitido)} emitido</span>
            </div>
            <div className="num mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-[var(--muted)]">
              <span>
                hoje{' '}
                <strong className="text-[var(--danger)]">{fmtPct(pct(m.emAberto, m.emitido))}</strong>
              </span>
              <span>
                no venc. <strong className="text-[var(--fg)]">
                  {fmtPct(pct(m.pagoAtrasado + m.emAberto, m.emitido))}
                </strong>
              </span>
              <span>
                recup.{' '}
                <strong className="text-[var(--success)]">
                  {fmtPct(pct(m.pagoAtrasado, m.pagoAtrasado + m.emAberto))}
                </strong>
              </span>
              <span>{moeda(m.emAberto)} em aberto</span>
            </div>
          </li>
        ))}
      </ul>

      <p className="border-t border-[var(--border)] px-5 py-3 text-xs text-[var(--faint)]">
        <strong>No vencimento</strong>: o que não estava pago no dia. <strong>Hoje</strong>: o que
        segue em aberto. <strong>Recuperação</strong>: do que atrasou, quanto já entrou. Inclui
        quem hoje está em revistoria — o atraso deles é da carteira.
      </p>
    </Card>
  );
}

const fmtPct = (v: number | null) => (v === null ? '—' : percentual(v, 1));

/**
 * Dia de vencimento: qual data atrasa mais. Os dias com pouco volume (menos
 * de 2% do emitido) se juntam em "outros" — são vencimentos avulsos, e
 * espalhados viram ruído.
 */
function DiasVencimento({
  dias,
  recorte,
  recortar,
}: {
  dias: DiaVencimento[];
  recorte: Recorte;
  recortar: (r: Recorte) => void;
}) {
  const total = dias.reduce((s, d) => s + d.emitido, 0);
  const relevantes = dias.filter((d) => total > 0 && d.emitido / total >= 0.02);
  // "outros" não entra na ordenação: é uma soma, fica sempre no fim.
  const { ordenados: principais, ordem, alternar } = useOrdenacao(
    relevantes,
    {
      dia: (d) => d.dia,
      atrasou: (d) => pct(d.atrasou, d.emitido),
      emAberto: (d) => pct(d.emAberto, d.emitido),
      placasHoje: (d) => d.placasEmAbertoHoje,
    },
    { coluna: 'dia', desc: false },
  );
  const t = { ordem, onOrdenar: alternar };
  const outros = dias.filter((d) => !relevantes.includes(d));
  const somaOutros = outros.reduce(
    (s, d) => ({
      emitido: s.emitido + d.emitido,
      atrasou: s.atrasou + d.atrasou,
      emAberto: s.emAberto + d.emAberto,
      placasEmAbertoHoje: s.placasEmAbertoHoje + d.placasEmAbertoHoje,
      valorEmAbertoHoje: s.valorEmAbertoHoje + d.valorEmAbertoHoje,
    }),
    { emitido: 0, atrasou: 0, emAberto: 0, placasEmAbertoHoje: 0, valorEmAbertoHoje: 0 },
  );
  const maxTaxa = Math.max(...dias.map((d) => pct(d.atrasou, d.emitido) ?? 0), 1);

  const linha = (
    chave: string,
    rotulo: string,
    d: Omit<DiaVencimento, 'dia'>,
    dia: number | null,
  ) => {
    const taxa = pct(d.atrasou, d.emitido) ?? 0;
    const ativo = dia !== null && recorte.tipo === 'dia' && recorte.dia === dia;
    const conteudo = (
      <>
        <span className="num font-medium text-[var(--fg)]">{rotulo}</span>
        <span className="flex items-center gap-2">
          <span className="h-2 min-w-0 flex-1 overflow-hidden rounded-[var(--r-full)] bg-[var(--gray-100)]">
            <span
              className="block h-full rounded-[var(--r-full)] bg-[var(--warn)]"
              style={{ width: `${(taxa / maxTaxa) * 100}%` }}
            />
          </span>
          <span className="num w-12 shrink-0 text-right text-[var(--fg)]">
            {percentual(taxa, 0)}
          </span>
        </span>
        <span className="num text-right whitespace-nowrap text-[var(--muted)]">
          {fmtPct(pct(d.emAberto, d.emitido))}
        </span>
        <span className="num text-right whitespace-nowrap text-[var(--muted)]">
          <strong className="text-[var(--fg)]">{d.placasEmAbertoHoje}</strong>
          <span className="hidden sm:inline"> · {moeda(d.valorEmAbertoHoje)}</span>
        </span>
      </>
    );
    const grade =
      'grid grid-cols-[52px_minmax(0,1fr)_72px_64px] items-center gap-3 rounded-[var(--r-md)] border border-transparent px-2 py-2 text-[13px] sm:grid-cols-[64px_minmax(0,1fr)_100px_160px]';
    return dia === null || d.placasEmAbertoHoje === 0 ? (
      <div key={chave} className={grade}>
        {conteudo}
      </div>
    ) : (
      <button
        key={chave}
        type="button"
        aria-pressed={ativo}
        onClick={() => recortar({ tipo: 'dia', dia })}
        className={cx(grade, 'anel-foco w-full text-left hover:bg-[var(--surface-hover)]', ativo && 'selecionado')}
      >
        {conteudo}
      </button>
    );
  };

  return (
    <Card className="p-5">
      <TituloSecao>Por dia de vencimento</TituloSecao>
      <div className="-mx-2 flex flex-col">
        <div className="grid grid-cols-[52px_minmax(0,1fr)_72px_64px] gap-3 px-2 pb-1 text-xs text-[var(--muted)] sm:grid-cols-[64px_minmax(0,1fr)_100px_160px]">
          <TituloOrdenavel coluna="dia" {...t}>Dia</TituloOrdenavel>
          <TituloOrdenavel coluna="atrasou" {...t}>Atrasou</TituloOrdenavel>
          <TituloOrdenavel coluna="emAberto" direita {...t}>Aberto</TituloOrdenavel>
          <TituloOrdenavel coluna="placasHoje" direita {...t}>Hoje</TituloOrdenavel>
        </div>
        {principais.map((d) => linha(String(d.dia), String(d.dia).padStart(2, '0'), d, d.dia))}
        {outros.length > 0 && linha('outros', 'outros', somaOutros, null)}
      </div>
      <p className="mt-3 text-xs text-[var(--faint)]">
        <strong>Atrasou</strong>: parte do emitido nesse dia que não foi paga no vencimento.{' '}
        <strong>Aberto</strong>: o que segue sem pagamento. <strong>Hoje</strong>: placas em
        cobrança daquele dia agora — toque para ver a lista.
      </p>
    </Card>
  );
}

// ─────────────────────────── Saíram com dívida ───────────────────────────

function ResumoFora({
  resumo,
  recorte,
  recortar,
}: {
  resumo: RespostaLista['resumo'];
  recorte: Recorte;
  recortar: (r: Recorte) => void;
}) {
  return (
    <>
      <Aviso>
        Inativos, negados e placas sem cadastro que deixaram boleto em aberto. Ficam fora das taxas
        da carteira: aqui é recuperação de dívida ou baixa.
      </Aviso>
      <GradeBento>
        <CartaoDado
          tamanho="largo"
          rotulo="Dívida de quem saiu"
          valor={moeda(resumo.valor)}
          nota={`${numero(resumo.placas)} placas · ${numero(resumo.titulos)} títulos`}
          Icone={Wallet}
          tom="perigo"
          ativo={recorte.tipo === 'todos'}
          onClick={() => recortar(TODOS)}
        />
        <CartaoDado
          tamanho="largo"
          rotulo="Nunca pagou"
          valor={numero(resumo.nuncaPagou)}
          nota="saiu sem quitar boleto nenhum"
          Icone={UserX}
          tom="perigo"
          ativo={recorte.tipo === 'nunca_pagou'}
          onClick={() => recortar({ tipo: 'nunca_pagou' })}
        />
      </GradeBento>
    </>
  );
}

// ─────────────────────────── Lista ───────────────────────────

function marcas(i: Item) {
  const m: { texto: string; tom: 'perigo' | 'alerta' }[] = [];
  if (i.classe === 'nunca_pagou') m.push({ texto: 'nunca pagou', tom: 'perigo' });
  if (i.ativoComAtraso) m.push({ texto: 'ativo com atraso', tom: 'alerta' });
  return m;
}

/**
 * Tabela a partir de 1024px, cartões abaixo — nunca rolagem horizontal.
 *
 * A tabela é `table-fixed` com larguras definidas: o nome do associado fica
 * com a sobra e trunca, em vez de empurrar as colunas de número para fora da
 * tela. Contato vai embaixo do nome; consultor só aparece quando há espaço
 * (1280px) e segue no detalhe.
 *
 * O resto — linha digitável, histórico, endereço — abre num modal ao tocar
 * na linha. Na lista fica só o que decide quem cobrar primeiro.
 */
function Tabela({ itens: brutos }: { itens: Item[] }) {
  const [detalhe, setDetalhe] = useState<Item | null>(null);
  const fechar = useCallback(() => setDetalhe(null), []);
  // Começa como a API entrega: quem está há mais tempo devendo primeiro.
  const { ordenados: itens, ordem, alternar, definir } = useOrdenacao(
    brutos,
    {
      associado: (i) => i.associado.nome,
      placa: (i) => i.placa,
      atraso: (i) => i.diasAtraso,
      valor: (i) => i.valor,
      consultor: (i) => i.consultor,
    },
    { coluna: 'atraso', desc: true },
  );
  const th = { ordem, onOrdenar: alternar };

  return (
    <>
      <Card className="hidden overflow-hidden lg:block">
        <table className="w-full table-fixed text-[13px]">
          <thead className="border-b border-[var(--border)] bg-[var(--bg-subtle)] text-left">
            <tr className="text-[var(--muted)]">
              <ThOrdenavel coluna="associado" {...th}>Associado</ThOrdenavel>
              <ThOrdenavel coluna="placa" className="w-32" {...th}>Placa</ThOrdenavel>
              <ThOrdenavel coluna="atraso" direita className="w-28" {...th}>Atraso</ThOrdenavel>
              <ThOrdenavel coluna="valor" direita className="w-32" {...th}>Valor</ThOrdenavel>
              <ThOrdenavel coluna="consultor" className="hidden w-48 xl:table-cell" {...th}>
                Consultor
              </ThOrdenavel>
              <th className="w-10 p-3">
                <span className="sr-only">Detalhes</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {itens.map((i) => (
              <tr
                key={`${i.placa}-${i.associado.codigo}`}
                onClick={() => setDetalhe(i)}
                className="group cursor-pointer border-b border-[var(--border)] transition-colors last:border-0 hover:bg-[var(--surface-hover)]"
              >
                <td className="p-3">
                  <p className="truncate font-medium text-[var(--fg)]" title={i.associado.nome ?? undefined}>
                    {i.associado.nome ?? '—'}
                  </p>
                  <p className="num truncate text-xs text-[var(--faint)]">
                    {formatarCpf(i.associado.cpf)}
                    {(i.associado.celular ?? i.associado.telefone) &&
                      ` · ${i.associado.celular ?? i.associado.telefone}`}
                  </p>
                  <Etiquetas itens={marcas(i)} />
                </td>
                <td className="p-3">
                  <p className="num text-[var(--fg)]">{i.placa ?? '—'}</p>
                  <p className="truncate text-xs text-[var(--faint)]">{i.veiculo.situacao ?? '—'}</p>
                </td>
                <td className="num p-3 text-right text-[var(--fg)]">
                  {i.diasAtraso}d
                  <span className="block text-[11px] text-[var(--faint)]">
                    {dataBr(i.vencimentoMaisAntigo)}
                  </span>
                </td>
                <td className="num p-3 text-right font-medium text-[var(--danger)]">
                  {moeda(i.valor)}
                  {/* Na frota o título cobre várias placas: quem vai cobrar
                      precisa do valor do boleto, não só da parte da placa. */}
                  {Math.round(i.valorTitulo) !== Math.round(i.valor) && (
                    <span className="block text-[11px] font-normal text-[var(--faint)]">
                      boleto {moeda(i.valorTitulo)}
                    </span>
                  )}
                </td>
                <td className="hidden p-3 text-[var(--muted)] xl:table-cell">
                  <span className="block truncate" title={i.consultor ?? undefined}>
                    {i.consultor ?? '—'}
                  </span>
                </td>
                <td className="p-3">
                  <ChevronRight
                    size={16}
                    aria-hidden
                    className="text-[var(--faint)] group-hover:text-[var(--fg)]"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <SeletorOrdem
        className="lg:hidden"
        ordem={ordem}
        onChange={definir}
        opcoes={[
          { coluna: 'atraso', rotulo: 'Dias de atraso' },
          { coluna: 'valor', rotulo: 'Valor' },
          { coluna: 'associado', rotulo: 'Associado' },
          { coluna: 'placa', rotulo: 'Placa' },
          { coluna: 'consultor', rotulo: 'Consultor' },
        ]}
      />
      <ul className="grid list-none gap-3 p-0 md:grid-cols-2 lg:hidden">
        {itens.map((i) => (
          <Card as="li" key={`${i.placa}-${i.associado.codigo}`} className="p-4">
            <button
              onClick={() => setDetalhe(i)}
              aria-haspopup="dialog"
              className="anel-foco -m-1 flex w-[calc(100%+8px)] items-start justify-between gap-3 rounded-[var(--r-md)] p-1 text-left"
            >
              <div className="min-w-0">
                <p className="truncate font-medium text-[var(--fg)]">{i.associado.nome ?? '—'}</p>
                <p className="num text-xs text-[var(--faint)]">
                  {i.placa ?? '—'} · {formatarCpf(i.associado.cpf)}
                </p>
              </div>
              <div className="flex shrink-0 items-start gap-1.5 text-right">
                <div>
                  <p className="num font-semibold text-[var(--danger)]">{moeda(i.valor)}</p>
                  <p className="num text-xs text-[var(--faint)]">
                    {i.diasAtraso}d · {i.titulos} tít.
                  </p>
                  {Math.round(i.valorTitulo) !== Math.round(i.valor) && (
                    <p className="num text-[11px] text-[var(--faint)]">
                      boleto {moeda(i.valorTitulo)}
                    </p>
                  )}
                </div>
                <ChevronRight size={18} aria-hidden className="mt-0.5 text-[var(--faint)]" />
              </div>
            </button>

            <Etiquetas itens={marcas(i)} />
            <BotoesContato associado={i.associado} compacto />
          </Card>
        ))}
      </ul>

      <Folha
        aberta={detalhe !== null}
        larga
        titulo={detalhe?.associado.nome ?? detalhe?.placa ?? ''}
        onFechar={fechar}
      >
        {detalhe && <Detalhe item={detalhe} />}
      </Folha>
    </>
  );
}

/** Tudo que se sabe do caso, para cobrar sem abrir o SGA — em abas. */
function Detalhe({ item: i }: { item: Item }) {
  const [copiada, setCopiada] = useState(false);

  async function copiarLinha() {
    if (!i.linhaDigitavel) return;
    try {
      await navigator.clipboard.writeText(i.linhaDigitavel);
      setCopiada(true);
    } catch {
      // Sem permissão de área de transferência (http na rede local): o texto
      // continua visível e selecionável logo acima.
    }
  }

  return (
    <div className="flex flex-col gap-5 p-1 text-[13px]">
      <div>
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <p className="num text-2xl font-semibold text-[var(--danger)]">{moeda(i.valor)}</p>
          <p className="text-[var(--muted)]">
            {i.diasAtraso} dias de atraso · {i.titulos} {i.titulos === 1 ? 'título' : 'títulos'} ·{' '}
            <span className="num">{i.placa}</span>
          </p>
        </div>
        <Etiquetas itens={marcas(i)} />
      </div>
      <BotoesContato associado={i.associado} />

      <AbasDetalhe
        abas={[
          {
            id: 'cobranca',
            rotulo: 'Cobrança',
            conteudo: (
              <div className="flex flex-col gap-5">
                <GradeDados>
                  <Dado rotulo="Valor da placa" valor={moeda(i.valor)} num />
                  {Math.round(i.valorTitulo) !== Math.round(i.valor) ? (
                    <Dado rotulo="Valor do boleto (frota)" valor={moeda(i.valorTitulo)} num />
                  ) : (
                    <Dado rotulo="Títulos em aberto" valor={numero(i.titulos)} num />
                  )}
                  <Dado rotulo="Vencido desde" valor={dataBr(i.vencimentoMaisAntigo)} num />
                  <Dado rotulo="Dias de atraso" valor={`${i.diasAtraso} dias`} num />
                  <Dado
                    rotulo="Histórico de boletos"
                    valor={`${i.boletosPagos} pagos · ${i.boletosEmAberto} em aberto`}
                  />
                  <Dado
                    rotulo="Perfil"
                    valor={i.classe === 'nunca_pagou' ? 'Nunca pagou' : 'Parou de pagar'}
                  />
                </GradeDados>

                {i.linhaDigitavel && (
                  <div className="rounded-[var(--r-md)] border border-[var(--border)] bg-[var(--bg-subtle)] p-3">
                    <p className="mb-1 text-xs text-[var(--muted)]">
                      Linha digitável do boleto mais antigo
                    </p>
                    <p className="num mb-3 text-[13px] break-all text-[var(--fg)] select-all">
                      {i.linhaDigitavel}
                    </p>
                    <Botao
                      tamanho="sm"
                      icone={copiada ? <Check size={14} /> : <Copy size={14} />}
                      onClick={() => void copiarLinha()}
                    >
                      {copiada ? 'Copiada' : 'Copiar linha digitável'}
                    </Botao>
                  </div>
                )}
              </div>
            ),
          },
          { id: 'associado', rotulo: 'Associado', conteudo: <DadosAssociado associado={i.associado} /> },
          {
            id: 'veiculo',
            rotulo: 'Veículo',
            conteudo: (
              <DadosVeiculo
                placa={i.placa}
                veiculo={i.veiculo}
                cooperativa={i.cooperativa}
                consultor={i.consultor}
              />
            ),
          },
        ]}
      />
    </div>
  );
}
